import React, { useState, useEffect, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import boost from "highcharts/modules/boost";
import Exporting from "highcharts/modules/exporting";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import Select from "react-select";
import { isEmpty } from "lodash";
import FilterDropdown from "../Dropdown/FilterDropdown";
import { Trans, useTranslation } from "react-i18next";
import {
    getSessionFilters,
    getInitialDateFilter,
    getMappedFilters,
} from "../../utils/FilterHelpers";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import Tippy from "@tippyjs/react";
import {
    COMPARE_FILTER_TYPE,
    FIELD_TYPES,
    LOGIC_TYPE,
    ROLES,
} from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import LookupsAPI from "../../API/lookups.api";
import {
    getInitialDates,
    longDate,
    shortDateTime,
} from "../../utils/DateHelper";
import DropdownButton from "../Buttons/DropdownButton";
import { useHistory } from "react-router-dom";
import { getObject, setObject } from "../../utils/StorageHelpers";
import BaseAlertModal from "../Modals/BaseAlertModal";
import { exportArrayToCSV } from "../../utils/FileHandler";
import dashboardApi from "../../API/dashboard.api";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";

Exporting(Highcharts);
NoDataToDisplay(Highcharts);
boost(Highcharts);

(function (H) {
    H.addEvent(H.Axis, "afterInit", function () {
        const logarithmic = this.logarithmic;

        if (logarithmic && this.options.custom.allowNegativeLog) {
            // Avoid errors on negative numbers on a log axis
            this.positiveValuesOnly = false;

            // Override the converter functions
            logarithmic.log2lin = (num) => {
                const isNegative = num < 0;

                let adjustedNum = Math.abs(num);

                if (adjustedNum < 10) {
                    adjustedNum += (10 - adjustedNum) / 10;
                }

                const result = Math.log(adjustedNum) / Math.LN10;
                return isNegative ? -result : result;
            };

            logarithmic.lin2log = (num) => {
                const isNegative = num < 0;

                let result = Math.pow(10, Math.abs(num));
                if (result < 10) {
                    result = (10 * (result - 1)) / (10 - 1);
                }
                return isNegative ? -result : result;
            };
        }
    });
})(Highcharts);

const TrendChart = ({
    title,
    dropdownOptions,
    trendData,
    isLoading,
    dropdownPlaceholder,
    refetchData,
    refetchLookup,
    component,
    entity,
}) => {
    const [parameters, setParameters] = useState([]);
    const [data, setData] = useState({});
    const [extraData, setExtraData] = useState({});
    const [supervisedUsersLookup, setSupervisedUsersLookup] = useState([]);
    const [affiliatesLookup, setAffiliatesLookup] = useState([]);
    const [selectedOption, setSelectedOption] = useState(
        () => getObject(`${entity}-analyticsSelections`, true) || null
    );
    const [threshold, setThreshold] = useState({});
    const [localFilter, setLocalFilter] = useState({});
    const [localAppliedFilters, setLocalAppliedFilters] = useState({});

    const [scanRedirectModal, setScanRedirectModal] = useState(false);

    const [filters, setFilters] = useState({});
    const [isExporting, setIsExporting] = useState(false);
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters(`${component}-filters`)
    );

    const history = useHistory();

    const container = useRef({});

    const { t } = useTranslation();

    const ENTITIES = {
        supplier: "supplier",
        lot: "lot",
        material: "material",
    };

    const searchFilters = [
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            monthBoundary: 3, //difference in months
        },
        {
            label: t("createdBy"),
            name: "createdBy",
            type: "Dropdown",
            options: supervisedUsersLookup,
            disabled: !isAuthorized(
                [ROLES.partnerAdmin, ROLES.support, ROLES.admin],
                getRoles()
            ),
        },
        {
            label: t("affiliates"),
            name: "organizationId",
            type: "Dropdown",
            options: affiliatesLookup,
            disabled: !isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
        },
        {
            label: t("lastReadings"),
            name: "lastReadings",
            type: "Input",
            inputType: "number",
            min: 1,
            max: 100,
        },
    ];

    const getMaterial = () => {
        return {
            material: {
                value:
                    trendData.materialId ||
                    selectedOption.materialId ||
                    selectedOption.value,
                label: trendData.materialName || selectedOption.label,
            },
        };
    };

    const verifyEntity = (entityName) => {
        return entity === entityName;
    };

    const getCreatedBy = () => {
        let createdBy = [];
        if (appliedFilters.createdBy) {
            let user = supervisedUsersLookup.find(
                (user) => appliedFilters.createdBy === user.value
            );
            createdBy.push({ value: user.value, label: user.email });
        }
        return createdBy;
    };

    const getCaptureDates = () => {
        return {
            capturedStartDate: appliedFilters.createdAt?.startDate || null,
            capturedEndDate: appliedFilters.createdAt?.endDate || null,
        };
    };

    const getLotsFilter = () => {
        return verifyEntity(ENTITIES.lot) && { lots: [{ ...selectedOption }] };
    };

    const getSuppliersFilter = () => {
        return (
            verifyEntity(ENTITIES.supplier) && {
                suppliers: [
                    {
                        label: selectedOption.supplierName,
                        value: selectedOption.supplierId,
                    },
                ],
            }
        );
    };

    const getThresholdRedirectBody = (target, compare, value, unit) => {
        return {
            ...getMaterial(),
            ...getCaptureDates(),
            threshold: {
                type: LOGIC_TYPE.and,
                filters: [
                    {
                        target,
                        compare,
                        value,
                        unit,
                    },
                ],
            },
            ...getLotsFilter(),
            ...getSuppliersFilter(),
            createdByUsers: getCreatedBy(),
        };
    };

    const getRangeThresholdRedirectBody = (filters) => {
        return {
            ...getMaterial(),
            ...getCaptureDates(),
            threshold: {
                type: LOGIC_TYPE.and,
                filters,
            },
            ...getLotsFilter(),
            ...getSuppliersFilter(),
            createdByUsers: getCreatedBy(),
        };
    };

    const getSampleNameRedirectBody = (sampleName = "") => {
        return {
            ...getMaterial(),
            ...getCaptureDates(),
            ...getLotsFilter(),
            ...getSuppliersFilter(),
            sampleName,
            createdByUsers: getCreatedBy(),
        };
    };

    const getExportData = (parameterName) => {
        const extraDataMaterialName = extraData?.materialName;

        const materialHeaders = [
            "Material",
            ...(verifyEntity(ENTITIES.lot) ? ["Lot"] : []),
            ...(verifyEntity(ENTITIES.supplier) ? ["Supplier"] : []),
            "Parameter",
            "Unit",
            "AVG",
            "SD",
            "CV",
            "Start Date",
            "End Date",
        ];

        const { start: defaultStartDate, end: defaultEndDate } =
            getInitialDates(3);

        const materialData = [
            extraDataMaterialName || selectedOption?.label,
            ...(extraDataMaterialName && verifyEntity(ENTITIES.lot)
                ? [selectedOption?.label]
                : []),
            ...(verifyEntity(ENTITIES.supplier)
                ? [selectedOption?.supplierName]
                : []),
            parameterName,
            data[parameterName]?.unit,
            data[parameterName]?.movingAverage,
            data[parameterName]?.standardDeviation,
            data[parameterName]?.coefficientOfVariation,
            appliedFilters?.createdAt?.startDate || defaultStartDate,
            appliedFilters?.createdAt?.startDate || defaultEndDate,
        ];

        const sampleHeaders = [
            "Sample Name",
            "Captured At (UTC)",
            "Results",
            "Created By",
        ];

        // TODO: convert the array to array of objects
        /*
            record[0] -> created at
            record[1] -> prediction value
            record[2] -> sample name
            record[3] -> created by
        */

        const sampleData = data[parameterName]?.values?.map((record) => {
            return [record[2], shortDateTime(record[0]), record[1], record[3]];
        });

        return [
            materialHeaders,
            materialData,
            [],
            sampleHeaders,
            ...sampleData,
        ];
    };

    const getDropdownButtonOptions = (parameterName) => [
        {
            label: `${t("aboveMaximum")} ${
                threshold[parameterName]?.max
                    ? `(${threshold[parameterName]?.max})`
                    : ""
            }`,
            icon: "arrow-up",
            disabled: !threshold[parameterName]?.max,
            callback: () => {
                setObject(
                    "scanFilter",
                    getThresholdRedirectBody(
                        parameterName,
                        COMPARE_FILTER_TYPE.greaterThan,
                        threshold[parameterName]?.max,
                        data[parameterName]?.unit
                    )
                );
                history.push("/scans/details");
            },
        },
        {
            label: `${t("belowMinimum")} ${
                threshold[parameterName]?.min
                    ? `(${threshold[parameterName]?.min})`
                    : ""
            }`,
            icon: "arrow-down",
            disabled: !threshold[parameterName]?.min,
            callback: () => {
                setObject(
                    "scanFilter",
                    getThresholdRedirectBody(
                        parameterName,
                        COMPARE_FILTER_TYPE.lessThan,
                        threshold[parameterName]?.min,
                        data[parameterName]?.unit
                    )
                );
                history.push("/scans/details");
            },
        },
        {
            label: `${t("withinRange")} ${
                threshold[parameterName]?.min && threshold[parameterName]?.max
                    ? `(${threshold[parameterName]?.min} - ${threshold[parameterName]?.max})`
                    : ""
            }`,
            icon: "sort-numeric-up-alt",
            disabled:
                !threshold[parameterName]?.min ||
                !threshold[parameterName]?.max,
            callback: () => {
                setObject(
                    "scanFilter",
                    getRangeThresholdRedirectBody([
                        {
                            target: parameterName,
                            compare: COMPARE_FILTER_TYPE.greaterThanOrEqual,
                            value: threshold[parameterName]?.min,
                            unit: data[parameterName]?.unit,
                        },
                        {
                            target: parameterName,
                            compare: COMPARE_FILTER_TYPE.lessThanOrEqual,
                            value: threshold[parameterName]?.max,
                            unit: data[parameterName]?.unit,
                        },
                    ])
                );
                history.push("/scans/details");
            },
            horizontalLine: true,
        },
        {
            label: t("exportCsv"),
            icon: "file-csv",
            disabled: isEmpty(data[parameterName].values),
            callback: () => {
                exportArrayToCSV(getExportData(parameterName), parameterName);
            },
        },
        {
            label: t(isExporting ? "requesting" : "exportXml"),
            icon: "file-alt",
            disabled: isExporting || isEmpty(data[parameterName].values),
            callback: () => {
                exportScansTrendXml(parameterName);
            },
        },
    ];

    const exportScansTrendXml = async (parameterName) => {
        setIsExporting(true);
        const materialId =
            verifyEntity(ENTITIES.material) || verifyEntity(ENTITIES.supplier)
                ? selectedOption?.materialId || selectedOption?.value
                : "";
        const lotId = verifyEntity(ENTITIES.lot) ? selectedOption?.value : "";
        const supplierId = verifyEntity(ENTITIES.supplier)
            ? selectedOption?.supplierId
            : "";
        const result = await dashboardApi.exportScansTrendXml(
            getMappedFilters({
                ...appliedFilters,
                materialId,
                lotId,
                supplierId,
                parameterName,
                modelParameterVersionId:
                    data[parameterName].modelParameterVersionId,
                modelParameterProductVersionId:
                    data[parameterName].modelParameterProductVersionId,
            })
        );
        if (result?.url) handleExternalLink(result?.url);
        setIsExporting(false);
    };

    const getLocalFilters = (parameterName) => [
        {
            label: t("controlLimits"),
            name: "threshold",
            fields: [
                {
                    label: t("min"),
                    name: "min",
                    type: "number",
                    max: localFilter[parameterName]?.threshold?.max || "",
                    placeholder: t("decimalPlacesPlaceholder"),
                },
                {
                    label: t("max"),
                    name: "max",
                    type: "number",
                    min: localFilter[parameterName]?.threshold?.min || "",
                    placeholder: t("thresholdPlaceholder"),
                },
            ],
            type: FIELD_TYPES.DoubleInput,
        },
    ];

    useEffect(() => {
        handleLocalStorageFilters();
        handleLookupsFetch();
    }, []);

    useEffect(() => {
        if (selectedOption) {
            refetchData(selectedOption, appliedFilters);
        }
    }, [selectedOption]);

    useEffect(() => {
        handleUpdateThreshold();
    }, [localAppliedFilters]);

    useEffect(() => {
        if (trendData.predictions && trendData.data) {
            setData(JSON.parse(JSON.stringify(trendData.data)));
            setExtraData({
                materialId: trendData?.materialId,
                materialName: trendData?.materialName,
            });
            setParameters([...trendData.predictions]);
            const predictionThreshold = {};
            const initialFilters = {};
            trendData.predictions.forEach((prediction) => {
                predictionThreshold[prediction] = {
                    min: null,
                    max: null,
                };
                initialFilters[prediction] = {};
            });
            setThreshold(predictionThreshold);
            setLocalFilter((prevFilters) => ({
                ...initialFilters,
                ...prevFilters,
            }));
            setLocalAppliedFilters((prevFilters) => ({
                ...initialFilters,
                ...prevFilters,
            }));
        }
    }, [trendData]);

    const getSupervisedUsersLookup = async () => {
        const result = await LookupsAPI.getSupervisedUsers();
        setSupervisedUsersLookup(formatSupervisedUsers(result));
    };

    const getAffiliatesLookup = async () => {
        const result = await LookupsAPI.getOrganizations();
        setAffiliatesLookup(formatAffilites(result));
    };

    const formatAffilites = (affiliates) => {
        return affiliates.map((affiliate) => {
            return {
                label: affiliate.name,
                value: affiliate.id,
            };
        });
    };

    const formatSupervisedUsers = (users) => {
        return users.map((user) => {
            return {
                label: (
                    <span>
                        {user.email}{" "}
                        <span className="text-xxs text-primary-color-orange">
                            ({user.organization.name})
                        </span>
                    </span>
                ),
                searchable: user.email,
                value: user.id,
                email: user.email,
            };
        });
    };

    const handleLocalStorageFilters = () => {
        const localStorageFilters = getSessionFilters(`${component}-filters`);
        if (!isEmpty(localStorageFilters)) {
            refetchLookup(localStorageFilters);
            setFilters(localStorageFilters);
            setAppliedFilters(localStorageFilters);
        } else {
            const filter = getInitialDateFilter();
            setFilters(filter);
            setAppliedFilters(filter);
            refetchLookup(filter);
        }
    };

    const handleLookupsFetch = () => {
        if (
            isAuthorized(
                [ROLES.partnerAdmin, ROLES.admin, ROLES.support],
                getRoles()
            )
        )
            getSupervisedUsersLookup();

        if (!isAuthorized([ROLES.partnerAdmin, ROLES.support], getRoles()))
            getAffiliatesLookup();
    };

    const getDateValues = (arr) => {
        try {
            const firstDate = arr[0][0];
            const lastDate = arr[arr.length - 1][0];
            return [longDate(firstDate), longDate(lastDate)];
        } catch (error) {
            console.log(error);
        }
    };

    const getUTCData = (values) => {
        return values.map((value) => {
            const newVal = [...value];
            newVal[0] = shortDateTime(newVal[0]);
            return newVal;
        });
    };

    const getChartOptions = (parameter) => {
        const parameterData = data[parameter].values || [];
        const [first, last] = getDateValues(parameterData);

        return {
            chart: { zoomType: "x" },
            title: {
                text: null,
            },
            xAxis: {
                categories: getDateValues(parameterData),
                labels: {
                    formatter: function () {
                        if (this.isFirst) {
                            return first;
                        } else if (this.isLast) {
                            return last;
                        } else {
                            return "";
                        }
                    },
                },
            },
            yAxis: {
                type: "logarithmic",
                custom: {
                    allowNegativeLog: true,
                },
                title: {
                    text: "Value",
                },
                plotLines: [
                    {
                        label: {
                            text: t("min"),
                        },
                        color: "red",
                        dashStyle: "LongDash",
                        value: threshold[parameter]?.min || null,
                        width: 1,
                        zIndex: 3,
                    },
                    {
                        label: {
                            text: t("max"),
                        },
                        color: "red",
                        dashStyle: "LongDash",
                        value: threshold[parameter]?.max || null,
                        width: 1,
                        zIndex: 3,
                    },
                ],
            },
            legend: {
                enabled: false,
            },
            tooltip: {
                pointFormat: `<b>${t(
                    "sampleName"
                )}</b>: {point.sampleName} <br/> <b>${t(
                    "createdBy"
                )}</b>: {point.createdBy} <br/> <b>${t(
                    "value"
                )}</b>: {point.y} {point.unit}`,
            },
            plotOptions: {
                series: {
                    keys: ["name", "y", "sampleName", "createdBy", "unit"],
                    point: {
                        events: {
                            click: function ({ point }) {
                                setScanRedirectModal({ ...point });
                            },
                        },
                    },
                },
            },
            series: [
                {
                    data: getUTCData(parameterData),
                    fillColor: {
                        stops: [
                            [0, Highcharts.getOptions().colors[0]],
                            [1, "white"],
                        ],
                    },
                    zIndex: 2,
                },
            ],
        };
    };

    const handleScansRedirection = () => {
        setObject(
            "scanFilter",
            getSampleNameRedirectBody(scanRedirectModal?.sampleName)
        );
        history.push("/scans/details");
        setScanRedirectModal(false);
    };

    const handleOptionSelection = (option) => {
        setSelectedOption(option);
        setObject(`${entity}-analyticsSelections`, option, true);
    };

    const applyFilters = (selectedFilters) => {
        refetchLookup(selectedFilters);
        if (selectedOption?.value) {
            refetchData(selectedOption, selectedFilters);
        }
    };

    const handleUpdateThreshold = () => {
        const updatedThreshold = {};
        Object.entries(localAppliedFilters).forEach(([key, value]) => {
            updatedThreshold[key] = value.threshold || {};
        });
        setThreshold(updatedThreshold);
    };

    const getCard = (parameterName) => {
        const details = [
            {
                label: "CV",
                tippy: "Coefficient of variation",
                name: "coefficientOfVariation",
            },
            { label: "AVG", tippy: "Average", name: "movingAverage" },
            {
                label: "SD",
                tippy: "Standard deviation",
                name: "standardDeviation",
            },
        ];

        const getParameterDetails = () => {
            const parameterData = data[parameterName];

            return details.map((detail, index) => {
                return (
                    <div className="flex flex-col items-center" key={index}>
                        <Tippy
                            content={detail.tippy || ""}
                            placement="top"
                            animation="scale"
                        >
                            <div className="text-white font-medium">
                                {detail.label}
                            </div>
                        </Tippy>
                        <div className="text-primary-color-orange font-medium text-2xl">
                            {Number(parameterData[detail.name]).toFixed(2)}
                        </div>
                    </div>
                );
            });
        };

        const filtersSetter = (parameterName, newValue) => {
            setLocalFilter((prev) => ({
                ...prev,
                [parameterName]: newValue,
            }));
        };

        const applieadFiltersSetter = (parameterName, newValue) => {
            setLocalAppliedFilters((prev) => ({
                ...prev,
                [parameterName]: newValue,
            }));
        };

        return (
            <div className="rounded-t-lg trend-card-color flex flex-col">
                <div className="flex flex-wrap justify-between w-full">
                    <div className="text-white pl-6 py-4 w-44">
                        {parameterName}
                    </div>
                    <div className="px-6 py-4 ml-auto flex justify-end items-center gap-1">
                        <FilterDropdown
                            component={`filter-trend--${entity}-${parameterName}`}
                            filterOptions={getLocalFilters(parameterName)}
                            filters={localFilter[parameterName]}
                            setFilters={(newValue) =>
                                filtersSetter(parameterName, newValue)
                            }
                            applyFilters={() => {}}
                            appliedFilters={localAppliedFilters[parameterName]}
                            setAppliedFilters={(newValue) =>
                                applieadFiltersSetter(parameterName, newValue)
                            }
                            saveToStorage={false}
                        />
                        <DropdownButton
                            icon={"fas fa-cog"}
                            buttonName={t("options")}
                            options={getDropdownButtonOptions(parameterName)}
                            disabled={
                                !(
                                    threshold[parameterName]?.max ||
                                    threshold[parameterName]?.min
                                ) && isEmpty(data[parameterName].values)
                            }
                            optionsClassNames="rounded-none py-1.5"
                        />
                    </div>
                </div>
                <div className="flex p-5 w-full justify-around self-center">
                    {getParameterDetails()}
                </div>
            </div>
        );
    };

    const renderChart = () => {
        const emptyPlaceholder = (fullWidth = false) => (
            <div className={`relative bg-white ${fullWidth ? "w-full" : ""}`}>
                <p className="chart-placeholder">
                    <Trans i18nKey="noScansDisplayMessage">
                        Get started by creating scans through Scan App
                    </Trans>
                </p>
            </div>
        );

        if (isEmpty(parameters)) return emptyPlaceholder(true);

        return (
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 3xl:grid-cols-3 w-full">
                {parameters?.map((parameter, index) => {
                    const options = getChartOptions(parameter);
                    if (!isEmpty(data) && !isEmpty(options)) {
                        return (
                            <div
                                className="w-full flex flex-col flex-auto border rounded-lg basis-1/2"
                                key={index}
                            >
                                {getCard(parameter)}
                                <div className="py-3 px-2 bg-white rounded-lg">
                                    <HighchartsReact
                                        ref={container}
                                        highcharts={Highcharts}
                                        options={options}
                                    />
                                </div>
                            </div>
                        );
                    } else {
                        return emptyPlaceholder();
                    }
                })}
            </div>
        );
    };

    const formatGroupLabel = (data) => {
        const groupBadgeStyles = {
            backgroundColor: "#EBECF0",
            borderRadius: "2em",
            color: "#172B4D",
            display: "inline-block",
            fontSize: 12,
            fontWeight: "normal",
            lineHeight: "1",
            minWidth: 1,
            padding: "0.16666666666667em 0.5em",
            textAlign: "center",
        };

        return (
            <div className="flex justify-between items-center mb-2 border-b-2 pb-2 text-primary-color-blue normal-case">
                <span className="text-tiny font-medium">{data.label}</span>
                <span style={groupBadgeStyles}>{data.options.length}</span>
            </div>
        );
    };
    const getReactStyle = () => {
        switch (entity) {
            case "supplier":
                return {
                    option: (styles) => ({
                        ...styles,
                        paddingLeft: 30,
                    }),
                    singleValue: (styles, { data }) => ({
                        ...styles,
                        ":before": {
                            marginRight: 4,
                            color: "hsl(24deg 100% 50%)",
                            content: `"[${data.supplierName}]"`,
                        },
                    }),
                };

            default:
                return {};
        }
    };

    return (
        <>
            <LoadingOverlay active={isLoading}>
                <div className="card-container shadow mb-6">
                    <header className="main-content-header flex items-center justify-between">
                        <h3 className="card-title w-48 whitespace-nowrap capitalize">
                            {title}
                        </h3>
                        <div className="action-header-wrapper flex items-center ml-auto">
                            <FilterDropdown
                                filterOptions={searchFilters}
                                filters={filters}
                                setFilters={setFilters}
                                applyFilters={applyFilters}
                                component={component}
                                appliedFilters={appliedFilters}
                                setAppliedFilters={setAppliedFilters}
                            />
                        </div>
                    </header>
                    <div className="flex items-center mb-4 mt-4">
                        <p className="w-42 m-0 whitespace-nowrap">
                            <Trans i18nKey="parametersTrendFor">
                                Parameters Trend for
                            </Trans>
                        </p>
                        <Select
                            onChange={handleOptionSelection}
                            className="ml-3 w-full"
                            isSearchable={true}
                            options={dropdownOptions}
                            placeholder={dropdownPlaceholder}
                            value={selectedOption}
                            theme={getReactSelectTheme}
                            styles={getReactStyle()}
                            formatGroupLabel={formatGroupLabel}
                        />
                    </div>
                    <div className="flex justify-center flex-wrap gap-4">
                        {renderChart()}
                    </div>
                </div>
            </LoadingOverlay>
            <BaseAlertModal
                isVisible={scanRedirectModal}
                setIsVisible={setScanRedirectModal}
                iconClassName="fas fa-external-link-square-alt"
                title={t("showDetails")}
                message={
                    <span>
                        {t("showDetailsDescription")}
                        <span className="font-bold">
                            {scanRedirectModal && scanRedirectModal?.sampleName}
                        </span>
                        ?
                    </span>
                }
                action={{
                    name: t("show"),
                    submitAction: handleScansRedirection,
                }}
                type="confirmation"
            />
        </>
    );
};

export default TrendChart;
